import { useWeb3React } from '@web3-react/core';
import { hooks } from '../connectors';
import { Connector } from '@web3-react/types';
import { LegacyWeb3ReactInterface, Web3Error } from '../connectors/types';
import { useInjectedWalletIfAvailable } from '../connectors/InjectedWalletContext';
import {
  WalletType,
  getWalletProvider,
  detectWalletType,
} from '../connectors/walletDetection';

/**
 * Main compatibility hook to replace old useWeb3React() calls
 * This provides a v6-like interface using v8 infrastructure
 *
 * Also checks the InjectedWalletContext for connections made through
 * the multi-wallet button (fixes postMessage cloning error - GitLab #4)
 */
export const useWeb3ReactLegacy = (): LegacyWeb3ReactInterface => {
  // Check injected wallet context first (for Rabby, Brave, etc.)
  const injectedWallet = useInjectedWalletIfAvailable();

  // Use the backward compatibility hook from v8
  const web3React = useWeb3React();

  // For MetaMask-specific operations, use MetaMask hooks
  const {
    useAccount: useMetaMaskAccount,
    useChainId: useMetaMaskChainId,
    useIsActive: useMetaMaskActive,
    useIsActivating: useMetaMaskActivating,
  } = hooks.metaMask;

  const metaMaskAccount = useMetaMaskAccount();
  const metaMaskChainId = useMetaMaskChainId();
  const metaMaskActive = useMetaMaskActive();
  const metaMaskActivating = useMetaMaskActivating();

  // Prioritize injected wallet if connected (for multi-wallet support)
  const injectedConnected = injectedWallet?.isConnected ?? false;
  const injectedAccount = injectedWallet?.accounts?.[0];
  const injectedChainId = injectedWallet?.chainId;
  const injectedProvider = injectedWallet?.getProvider?.();

  // Get the correct provider - prioritize injected wallet provider
  const provider = getWeb3Provider({
    injectedProvider,
    web3ReactProvider: web3React.provider,
  });

  return {
    // Prioritize injected wallet, then Web3React, then MetaMask
    account: injectedAccount || web3React.account || metaMaskAccount,
    chainId: injectedChainId || web3React.chainId || metaMaskChainId,
    active: injectedConnected || web3React.isActive || metaMaskActive,
    error: injectedWallet?.error, // Include injected wallet errors
    activating: metaMaskActivating || false,
    connector: web3React.connector,
    provider, // Raw provider for transactions

    // v6-style activation method
    activate: async (connector: Connector) => {
      try {
        // Check if connector is null (disabled)
        if (!connector) {
          throw new Error('CONNECTOR_UNAVAILABLE');
        }

        await connector.activate();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Wallet activation failed:', error);

        const err = error as { message?: string };
        // Handle null connector case
        if (err.message === 'CONNECTOR_UNAVAILABLE') {
          throw new Error(Web3Error.ACTIVATION_ERROR);
        }

        // Map common errors to our error types
        if (err.message?.includes('User rejected')) {
          throw new Error(Web3Error.USER_REJECTED);
        } else if (err.message?.includes('No Ethereum provider')) {
          throw new Error(Web3Error.NO_PROVIDER);
        } else if (err.message?.includes('Unsupported chain')) {
          throw new Error(Web3Error.UNSUPPORTED_CHAIN);
        } else {
          throw new Error(Web3Error.ACTIVATION_ERROR);
        }
      }
    },

    // v6-style deactivation method
    deactivate: () => {
      try {
        // Disconnect injected wallet if connected
        if (injectedWallet?.isConnected && injectedWallet.disconnect) {
          injectedWallet.disconnect();
        }
        // Also deactivate web3-react connector
        if (web3React.connector?.deactivate) {
          web3React.connector.deactivate();
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Wallet deactivation failed:', error);
      }
    },
  };
};

/**
 * Network helper functions for better UX
 */
export const getNetworkName = (chainId?: number): string => {
  switch (chainId) {
    case 369:
      return 'PulseChain';
    case 943:
      return 'PulseChain Testnet v4';
    case 942:
      return 'PulseChain Testnet v3';
    case 1:
      return 'Ethereum Mainnet';
    case 5:
      return 'Goerli';
    case 3:
      return 'Ropsten';
    default:
      return `Unknown Network ${chainId ? `(${chainId})` : ''}`;
  }
};

export const getNetworkCurrency = (chainId?: number): string => {
  switch (chainId) {
    case 369:
      return 'PLS';
    case 943:
    case 942:
      return 'tPLS';
    case 1:
    case 5:
    case 3:
      return 'ETH';
    default:
      return 'Unknown';
  }
};

export const isTestnet = (chainId?: number): boolean => {
  return chainId === 943 || chainId === 942 || chainId === 5 || chainId === 3;
};

export const isPulseChain = (chainId?: number): boolean => {
  return chainId === 369 || chainId === 943 || chainId === 942;
};

/**
 * Connection status helpers
 */
export const getConnectionStatus = (
  active: boolean,
  activating: boolean,
  error?: Error
): 'connected' | 'connecting' | 'disconnected' | 'error' => {
  if (error) return 'error';
  if (activating) return 'connecting';
  if (active) return 'connected';
  return 'disconnected';
};

/**
 * Error message formatter for user-friendly display
 */
export const formatWeb3Error = (error?: Error): string => {
  if (!error) return '';

  const message = error.message.toLowerCase();

  if (message.includes('user rejected') || message.includes('user denied')) {
    return 'Connection was cancelled. Please try again.';
  } else if (
    message.includes('no ethereum provider') ||
    message.includes('no provider')
  ) {
    return 'No wallet detected. Please install MetaMask or connect WalletConnect.';
  } else if (
    message.includes('unsupported chain') ||
    message.includes('wrong network')
  ) {
    return 'Please switch to the correct network in your wallet.';
  } else if (message.includes('unauthorized')) {
    return 'Wallet connection unauthorized. Please try connecting again.';
  } else {
    return 'Connection failed. Please check your wallet and try again.';
  }
};

/**
 * Shortcut functions for common operations
 */
export const truncateAddress = (address: string, chars = 4): string => {
  if (!address) return '';
  return `${address.slice(0, chars + 2)}...${address.slice(-chars)}`;
};

export const formatBalance = (balance: string, decimals = 4): string => {
  const num = parseFloat(balance);
  if (isNaN(num)) return '0';
  return num.toFixed(decimals);
};

/**
 * Get the correct provider to use for Web3 operations.
 *
 * This function resolves the postMessage cloning error (GitLab issue #4) by
 * prioritizing the injected wallet provider (from InjectedWalletContext) over
 * the web3-react provider when both are available.
 *
 * The bug: When connecting via Rabby through the multi-wallet button, the old code
 * would create Web3Provider from useWeb3React().provider which goes through postMessage
 * and causes "Failed to execute 'postMessage' on 'Window'" errors because the provider
 * contains non-serializable callback functions.
 *
 * The fix: Use the direct EIP-1193 provider from InjectedWalletContext when available,
 * or use proper wallet detection to get the correct provider when multiple wallets are installed.
 */
export const getWeb3Provider = ({
  injectedProvider,
  web3ReactProvider,
}: {
  injectedProvider: unknown;
  web3ReactProvider: unknown;
}): unknown => {
  // Prioritize injected wallet provider (Rabby, Brave, etc. via multi-wallet button)
  // This avoids the postMessage cloning error
  if (injectedProvider) {
    return injectedProvider;
  }

  // For wallets connected via web3-react (MetaMask button),
  // use proper wallet detection to get the correct provider
  // This handles cases where multiple wallets are installed
  if (typeof window !== 'undefined') {
    const detectedType = detectWalletType();

    // Try to get the specific provider for the detected wallet
    const specificProvider = getWalletProvider(detectedType);
    if (specificProvider) {
      return specificProvider;
    }

    // If detection fails, try window.ethereum directly
    const ethereum = (window as { ethereum?: unknown }).ethereum;
    if (ethereum) {
      return ethereum;
    }
  }

  // Fall back to web3-react provider
  return web3ReactProvider;
};
